/**
 * @fileoverview Popover to display linked costs for a payment.
 * @module features/payments/components/LinkedCostsPopover
 */

import { ReactElement, useState } from 'react'
import { format } from 'date-fns'
import { AlertCircle, Link as LinkIcon, Link2Off, Loader2, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Button, Money } from '@/shared/components/ui'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui'
import { usePaymentLinkedCosts } from '../hooks/usePaymentLinkedCosts'
import type { PaymentDTO, PaymentsPageResponse } from '../schemas/paymentsSchema'
import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query'
import { unlinkPaymentFromCost } from '@/features/indirect-costs/api/indirectCostsLinksService'
import { toast } from 'sonner'
import { updatePaymentLinkingRequirement } from '../api/linkingRequirementService'
import { cn } from '@/shared/lib/utils'

export interface LinkedCostsPopoverProps {
  payment: PaymentDTO
  /** Optional className forwarded to the trigger button for layout control */
  className?: string
  /** Callback to trigger link-to-cost flow */
  onLinkToCost?: () => void
  /** Render a low-emphasis trigger style for dense list UIs */
  quiet?: boolean
}

/**
 * Popover component that displays linked costs for a payment.
 *
 * @component
 */
export default function LinkedCostsPopover({
  payment,
  className,
  onLinkToCost,
  quiet = false,
}: LinkedCostsPopoverProps): ReactElement {
  const { t } = useTranslation('features-payments')
  const { t: tCommon } = useTranslation('common')
  const queryClient = useQueryClient()
  const [unlinkConfirmUuid, setUnlinkConfirmUuid] = useState<string | null>(null)
  const [open, setOpen] = useState<boolean>(false)

  // Lazy-load details only when popover is open to avoid per-row network calls
  const {
    data: linkedCosts,
    isLoading,
    error,
  } = usePaymentLinkedCosts(payment.id, { enabled: open })
  const costs = linkedCosts?.indirectCosts || []

  const unlinkMutation = useMutation({
    mutationFn: async (costUuid: string) => {
      await unlinkPaymentFromCost(costUuid, payment.id)
    },
    onSuccess: () => {
      toast.success(t('linkedPayments.unlink.success', { ns: 'features-indirect-costs' }))
      void queryClient.invalidateQueries({ queryKey: ['payments', 'linked-costs', payment.id] })
      void queryClient.invalidateQueries({ queryKey: ['payments'] })
      setUnlinkConfirmUuid(null)
    },
    onError: (err: unknown) => {
      const message =
        err instanceof Error
          ? err.message
          : t('linkedPayments.unlink.error', { ns: 'features-indirect-costs' })
      toast.error(message)
    },
  })

  const markNotNeededMutation = useMutation({
    mutationFn: async () => updatePaymentLinkingRequirement(payment.id, 'NOT_NEEDED'),
    onSuccess: () => {
      toast.success(t('linkingRequirement.toasts.notNeededSuccess'))
      queryClient.setQueriesData<InfiniteData<PaymentsPageResponse>>(
        { queryKey: ['payments'] },
        (oldData: InfiniteData<PaymentsPageResponse> | undefined) => {
          if (!oldData || !oldData.pages || !Array.isArray(oldData.pages)) return oldData
          return {
            ...oldData,
            pages: oldData.pages.map((page: PaymentsPageResponse) => ({
              ...page,
              content: page.content.map(p => {
                if (p.id === payment.id) {
                  return { ...p, costLinkingRequirement: 'NOT_NEEDED' }
                }
                return p
              }),
            })),
          }
        }
      )
      void queryClient.invalidateQueries({ queryKey: ['payments', 'linked-costs', payment.id] })
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : t('linkingRequirement.toasts.error')
      toast.error(message)
    },
  })

  const markRequiredMutation = useMutation({
    mutationFn: async () => updatePaymentLinkingRequirement(payment.id, 'REQUIRED'),
    onSuccess: () => {
      toast.success(t('linkingRequirement.toasts.requiredSuccess'))
      queryClient.setQueriesData<InfiniteData<PaymentsPageResponse>>(
        { queryKey: ['payments'] },
        (oldData: InfiniteData<PaymentsPageResponse> | undefined) => {
          if (!oldData || !oldData.pages || !Array.isArray(oldData.pages)) return oldData
          return {
            ...oldData,
            pages: oldData.pages.map((page: PaymentsPageResponse) => ({
              ...page,
              content: page.content.map(p => {
                if (p.id === payment.id) {
                  return { ...p, costLinkingRequirement: 'REQUIRED' }
                }
                return p
              }),
            })),
          }
        }
      )
      void queryClient.invalidateQueries({ queryKey: ['payments', 'linked-costs', payment.id] })
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : t('linkingRequirement.toasts.error')
      toast.error(message)
    },
  })

  const handleOpenChange = (next: boolean): void => {
    setOpen(next)
    if (!next) setUnlinkConfirmUuid(null)
  }

  const linkedCostCount = payment.linkedIndirectCostCount ?? 0
  const hasLinks = linkedCostCount > 0
  const isNotNeeded = payment.costLinkingRequirement === 'NOT_NEEDED'
  const needsAttention = !isNotNeeded && !hasLinks
  const defaultLabel = isNotNeeded
    ? t('linkingRequirement.tooltipNotNeeded')
    : hasLinks
      ? t('linkedCosts.count', { count: linkedCostCount })
      : t('linkedCosts.empty')
  const defaultIcon = isNotNeeded ? (
    <Link2Off className="w-4 h-4 text-muted-foreground" />
  ) : (
    <LinkIcon className="w-4 h-4 text-muted-foreground" />
  )
  const defaultBadge = !isNotNeeded && hasLinks ? linkedCostCount : null
  const triggerAriaLabel = isNotNeeded
    ? t('linkingRequirement.tooltipNotNeeded')
    : t('aria.viewLinkedCosts')

  type TriggerOverrides = {
    label?: string
    icon?: ReactElement
    disabled?: boolean
    badge?: number | null
    useStatusColor?: boolean
  }

  const renderTrigger = ({
    label = defaultLabel,
    icon = defaultIcon,
    disabled = false,
    badge = defaultBadge,
    useStatusColor = !quiet,
  }: TriggerOverrides = {}): ReactElement => {
    const toneClass = useStatusColor
      ? needsAttention
        ? 'text-yellow-500 border-yellow-500/60'
        : isNotNeeded
          ? 'text-muted-foreground border-border'
          : ''
      : ''
    const accessibleLabel = label ?? defaultLabel

    return (
      <Button
        variant="outline"
        size="sm"
        type="button"
        aria-label={triggerAriaLabel}
        className={cn('px-2 w-auto min-w-[2.5rem] justify-center font-normal', toneClass, className)}
        disabled={disabled}
        title={accessibleLabel}
      >
        <span className="inline-flex items-center gap-2 text-sm">
          <span className="inline-flex items-center">{icon}</span>
          {typeof badge === 'number' && badge > 0 ? (
            <span className="text-xs text-muted-foreground font-semibold">{badge}</span>
          ) : null}
          <span className="sr-only">{accessibleLabel}</span>
        </span>
      </Button>
    )
  }

  if (isLoading) {
    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          {renderTrigger({
            label: t('linkedCosts.loading'),
            icon: <Loader2 className="w-4 h-4 animate-spin" />,
            disabled: true,
            badge: null,
            useStatusColor: false,
          })}
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4 rounded-lg shadow-lg" align="end" sideOffset={8}>
          <div className="text-center text-sm text-muted-foreground">
            {t('linkedCosts.loading')}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  if (error) {
    return (
      <Popover open={open} onOpenChange={handleOpenChange}>
        <PopoverTrigger asChild>
          {renderTrigger({
            label: t('linkedCosts.loadError'),
            icon: <AlertCircle className="w-4 h-4 text-destructive" />,
            badge: null,
            useStatusColor: false,
          })}
        </PopoverTrigger>
        <PopoverContent className="w-80 p-4 rounded-lg shadow-lg" align="end" sideOffset={8}>
          <div className="text-center text-sm text-destructive">
            {error instanceof Error ? error.message : t('linkedCosts.loadError')}
          </div>
        </PopoverContent>
      </Popover>
    )
  }

  // Show confirm when requirement is not NOT_NEEDED and there are no links (use precomputed count)
  const showConfirm = !isNotNeeded && linkedCostCount === 0

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{renderTrigger()}</PopoverTrigger>
      <PopoverContent
        className={
          showConfirm || isNotNeeded
            ? 'w-auto p-4 rounded-lg shadow-lg'
            : 'w-[32rem] p-0 rounded-lg shadow-lg'
        }
        align="end"
        sideOffset={8}
        side="bottom"
      >
        {showConfirm ? (
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-muted-foreground">
              This payment has no linked costs
            </p>
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  setOpen(false)
                  void markNotNeededMutation.mutateAsync()
                }}
                disabled={markNotNeededMutation.isPending}
              >
                Mark as not needed
              </Button>
              {onLinkToCost && (
                <Button
                  type="button"
                  size="sm"
                  onClick={() => {
                    setOpen(false)
                    onLinkToCost()
                  }}
                >
                  Link to a cost
                </Button>
              )}
            </div>
          </div>
        ) : (
          !isNotNeeded && (
            <div className="p-4 border-b border-border hidden">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-foreground">{t('linkedCosts.title')}</h3>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {t('linkedCosts.count', {
                  count: linkedCosts ? costs.length : (payment.linkedIndirectCostCount ?? 0),
                })}
              </p>
            </div>
          )
        )}

        {!isNotNeeded && !showConfirm && (
          <div className="max-h-80 overflow-auto">
            {costs.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <LinkIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('linkedCosts.empty')}</p>
              </div>
            ) : (
              <div className="p-4">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        {t('linkedCosts.table.columns.number')}
                      </TableHead>
                      <TableHead>{t('linkedCosts.title')}</TableHead>
                      <TableHead className="w-32 text-right">
                        {t('linkedCosts.table.columns.amount')}
                      </TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {costs.map((cost, idx) => (
                      <TableRow key={cost.costUuid}>
                        <TableCell className="text-sm">{idx + 1}</TableCell>
                        <TableCell className="max-w-[16rem]">
                          <div className="flex flex-col">
                            <span className="text-sm truncate" title={cost.subcategoryDisplayName}>
                              {cost.subcategoryDisplayName}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {(() => {
                                const d = new Date(cost.effectiveFrom)
                                const day = format(d, 'dd')
                                const monthShort = [
                                  'jan',
                                  'feb',
                                  'mar',
                                  'apr',
                                  'may',
                                  'jun',
                                  'jul',
                                  'aug',
                                  'sep',
                                  'oct',
                                  'nov',
                                  'dec',
                                ][d.getMonth()]
                                return `${day} ${tCommon(`monthsShort.${monthShort}`)}`
                              })()}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-right">
                          <Money
                            value={cost.contributionAmount}
                            currency={payment.currency}
                            minFraction={2}
                            maxFraction={2}
                          />
                        </TableCell>
                        <TableCell>
                          {unlinkConfirmUuid === cost.costUuid ? (
                            <button
                              type="button"
                              className="text-xs text-destructive hover:bg-destructive/10 rounded px-1"
                              onClick={() => unlinkMutation.mutate(cost.costUuid)}
                              disabled={unlinkMutation.isPending}
                            >
                              {t('actions.confirm')}
                            </button>
                          ) : (
                            <button
                              type="button"
                              aria-label={t('aria.unlinkPayment', {
                                ns: 'features-indirect-costs',
                              })}
                              className="text-destructive hover:bg-destructive/10 rounded p-1"
                              onClick={() => setUnlinkConfirmUuid(cost.costUuid)}
                              disabled={unlinkMutation.isPending}
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        )}

        {isNotNeeded && (
          <div className="flex flex-col items-start gap-3">
            <p className="text-sm text-muted-foreground">
              Linking to cost is not needed
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setOpen(false)
                void markRequiredMutation.mutateAsync()
              }}
              disabled={markRequiredMutation.isPending}
            >
              Mark as needed
            </Button>
          </div>
        )}

        {/* Removed footer summary as requested */}
      </PopoverContent>
    </Popover>
  )
}
